import { useSearchParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';
import moment from 'moment';

export interface IFilter<T> {
    createFromAny(input: any): T;
}

export default function useFilter<T extends IFilter<T>>(defaults: T) {
    const [searchParams, setSearchParams] = useSearchParams();
    const getFilterFromQuery: () => T = () => {
        const newFilter = Object.keys(defaults).reduce((a, c) => {
            const f = c as keyof T;
            a[c] = searchParams.get(<string>f) || defaults[f];
            return a;
        }, {} as any);

        return defaults.createFromAny(newFilter);
    };

    const [filter, setFilter] = useState<T>(getFilterFromQuery());
    const getQueryFromFilter = () => Object.keys(defaults).reduce((a, c) => {
        const f = c as keyof T;

        if (f === 'page') {
            const value: any = filter[f];
            if (value !== '1') {
                a[c] = value;
            }
        } else if (typeof filter[f] !== 'function') {
            const value: any = filter[f];
            if (value) {
                a[c] = value;
            }
        }
        return a;
    }, {} as any);

    const search = () => {
        const { createFromAny: notNeededOne, ...restOfFilter } = filter;
        const { createFromAny: notNeededTwo, ...requestOfQuery } = getFilterFromQuery();
        if (!shallowEqual(restOfFilter, requestOfQuery)) {
            const newQuery = getQueryFromFilter();
            setSearchParams(newQuery);
        }
    };

    useEffect(() => {
        setFilter(defaults.createFromAny(getFilterFromQuery()));
    }, [searchParams]);

    const handleFilterChange: (event: React.ChangeEvent<HTMLInputElement> | {
        target: { name: string, value: any }
    }) => void = (e) => {
        const field = e.target.name as keyof T;
        const { value } = e.target;
        filter[field] = moment.isMoment(value) ? value.toISOString() : value;

        setFilter(defaults.createFromAny({ ...filter, page: '1' }));
    };

    const handleFilterChangeThenSearch: (event: React.ChangeEvent<HTMLInputElement> | {
        target: { name: string, value: any }
    }) =>
        void = (e) => {
        handleFilterChange(e);
        search();
    };

    const clearFilter = () => {
        setSearchParams({});
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        const qry = getQueryFromFilter();
        setSearchParams({
            ...qry,
            page: `${value}`,
        });
    };

    const handleFilterToggle: (fieldName: string) => void = (fieldName: string) => {
        const field = fieldName as keyof T;
        const value = filter[field] as any;
        const toggled = value === 'true' ? '' : 'true';
        setFilter({ ...filter, [fieldName]: toggled });
    };

    const getValue = () => getFilterFromQuery();

    const filteredItemsCount = () => {
        const knownKeys = Object.keys(defaults);
        const knownParams = Array.from(searchParams.keys())
            .filter((key) => knownKeys.includes(key) && key !== 'page')
            .filter((key) => searchParams.get(key)); // Only count non-empty values

        return knownParams.length;
    };

    return {
        filter,
        getValue,
        search,
        handleFilterChange,
        handleFilterChangeThenSearch,
        handleFilterToggle,
        handlePageChange,
        clearFilter,
        filteredItemsCount,
    };
}
